import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PageAlert from "../../components/PageAlert/PageAlert";
import {
  createProduct,
  deleteProduct,
  deleteProductImage,
  fetchProduct,
  fetchProducts,
  makeSlug,
  normalizeApplications,
  normalizeSpecs,
  normalizeStandards,
  updateProduct,
  uploadProductImages,
} from "../../api/productApi";
import { fetchCategories, fetchCategory } from "../../api/categoryApi";
import { resolveApiError } from "../../utils/apiError";
import "./product-detail.scss";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const asFlag = (value) => value === true || value === 1 || value === "1";
const productRecord = (payload) => payload?.product ?? payload;
const relatedProductsFromPayload = (payload) => {
  const options = payload?.form_options?.related_products;
  return Array.isArray(options) ? options.filter((item) => item?.id != null) : [];
};
const relatedIdsFromValue = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => (item && typeof item === "object" ? Number(item.id) : Number(item)))
    .filter((id) => Number.isFinite(id));
const createdProductId = (payload) => {
  const record = productRecord(payload);
  const id = Number(record?.id ?? payload?.id);
  return Number.isFinite(id) ? id : null;
};
const relatedOptionsFromProducts = (products = []) =>
  (Array.isArray(products) ? products : [])
    .filter((item) => item?.id != null)
    .map((item) => ({
      id: item.id,
      name: item.name,
      sku: item.sku,
      category_id: item.category_id ?? item.category?.id,
    }));
const imagesFromPayload = (saved, current = []) => {
  if (Array.isArray(saved?.images)) return saved.images;
  if (Array.isArray(saved)) return saved;
  const image = saved?.image;
  if (image?.id || image?.url) {
    return [...current.filter((item) => item.id !== image.id), image];
  }
  return current;
};

const productTemplates = {
  "1": {
    name: "HT Deformed Bar", nameZh: "高强度螺纹钢筋", slug: "ht-deformed-bar", sku: "STL-REB-1040", category: "Steel Bars 钢筋",
    overview: "High-tensile deformed reinforcement bar conforming to MS146 Grade 460B — the standard structural rebar for reinforced concrete works across Sarawak's construction, civil and infrastructure sectors.",
    overviewZh: "符合MS146 460B级标准的高强度螺纹钢筋，是砂拉越建筑、土木及基础设施项目钢筋混凝土工程的标准结构钢筋。",
    popular: true,
    specs: [["Grade · 等级", "460B High Tensile", "楼板与梁"], ["Sizes · 直径", "Y10 – Y40", "多种规格可选"], ["Lengths · 长度", "6m / 9m / 12m", "标准长度"], ["Standard · 标准", "MS146 / BS4449", "符合国标"]],
    standards: [["Malaysian Std", "MS146:2006"], ["British Std", "BS4449"], ["High tensile", "Grade 460B"], ["Gov. works", "JKR Approved"], ["Certified", "SIRIM"]],
    applications: ["Reinforced concrete slabs & beams 楼板与梁", "Foundations & pile caps 地基与桩帽", "Retaining walls & columns 挡土墙与柱", "Road & bridge infrastructure 道路与桥梁工程"],
  },
  "2": {
    name: "Green Wheelie Bin", nameZh: "绿色滚轮垃圾桶", slug: "green-wheelie-bin", sku: "WHB-GRN-120", category: "Wheel Bin 垃圾桶",
    overview: "The Green Wheelie Bin is manufactured from high-quality, UV-stabilized High Density Polyethylene (HDPE), offering excellent durability for residential, commercial and industrial waste collection.",
    overviewZh: "绿色滚轮垃圾桶采用高品质、抗紫外线高密度聚乙烯（HDPE）制造，坚固耐用，适用于住宅、商业及工业废弃物收集。",
    popular: false,
    specs: [["Material · 材质", "High Density Polyethylene (HDPE)", "高密度聚乙烯"], ["Capacity · 容量", "100L / 120L / 240L & 360L", "提供多种容量选择"], ["Colour · 颜色", "Green", "绿色"], ["Standard · 标准", "EN 840 Compliant", "符合 EN 840 标准"]],
    standards: [["Malaysian Std", "MS EN 840"], ["British Std", "BS EN 840"], ["High tensile", "HDPE Virgin Grade"], ["Gov. works", "Local Authority Approved"], ["Certified", "EN 840 Certified"]],
    applications: ["Residential housing 住宅区", "Commercial buildings 商业建筑", "Shopping centres 购物中心", "Schools 学校", "Hospitals 医院", "Industrial facilities 工业厂房", "Municipal waste collection 市政垃圾收集"],
  },
};

const emptyProduct = { name:"", nameZh:"", slug:"", sku:"", category:"", categoryId:"", overview:"", overviewZh:"", meta_title:"", meta_description:"", meta_keyword:"", popular:false, featured:false, visible:true, whatsapp:true, enquiryEnabled:true, status:true, order:0, specs:[], standards:[], applications:[], relatedIds:[] };
const TextField = ({ label, value, onChange, textarea=false, help }) => <label className="detail-editor__field"><span>{label}</span>{textarea?<textarea value={value} onChange={onChange}/>:<input value={value} onChange={onChange}/>} {help&&<small>{help}</small>}</label>;
const CategoryField = ({ categories, value, onChange }) => (
  <label className="detail-editor__field">
    <span>Category</span>
    <div className="detail-editor__select">
      <select value={value} onChange={onChange}>
        <option value="">Uncategorised</option>
        {categories.map((category) => (
          <option value={category.id} key={category.id}>{category.name}</option>
        ))}
      </select>
      <Icon icon="ph:caret-down" height={14} />
    </div>
  </label>
);

const ImageUploadTile = ({ inputRef, label, hint, multiple = false, disabled, onChange }) => (
  <div className="detail-editor__image-slot">
    <input
      ref={inputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
      multiple={multiple}
      hidden
      onChange={onChange}
    />
    <button
      className="detail-editor__image-upload"
      type="button"
      disabled={disabled}
      onClick={() => inputRef.current?.click()}
    >
      <span className="detail-editor__image-upload-icon"><Icon icon="ph:plus" height={18} /></span>
      <strong>{label}</strong>
      <small>{hint}</small>
    </button>
  </div>
);

const ImagePreviewCard = ({ image, disabled, onDelete }) => (
  <div className="detail-editor__image-slot">
    <div className="detail-editor__image-upload detail-editor__image-upload--preview">
      <img src={image.url} alt="" />
      <button
        className="detail-editor__image-remove"
        type="button"
        aria-label="Remove image"
        disabled={disabled}
        onClick={() => onDelete(image)}
      >
        <Icon icon="ph:x" height={18} />
      </button>
    </div>
  </div>
);

const RelatedProductsList = ({ products, selectedIds, onToggle }) => {
  if (!products.length) {
    return <p className="detail-editor__related-empty">No related products are available for this category.</p>;
  }

  return products.map((item) => {
    const id = Number(item.id);
    const checked = selectedIds.some((selected) => Number(selected) === id);
    return (
      <label className="detail-editor__related" key={item.id}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onToggle(id, event.target.checked)}
        />
        <span>
          <strong>{item.name}</strong>
          <small>{item.sku || "No SKU"}</small>
        </span>
      </label>
    );
  });
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const isCreate = !productId;
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(emptyProduct);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("content");
  const [notice, setNotice] = useState("");
  const [images, setImages] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingBase, setPendingBase] = useState(null);
  const [pendingThumbnails, setPendingThumbnails] = useState([]);
  const baseImageInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const slugManualRef = useRef(false);
  const setField = (field, value) => setProduct((current) => {
    const next = { ...current, [field]: value };
    if (isCreate && field === "name" && !slugManualRef.current) {
      next.slug = makeSlug(value);
    }
    if (field === "slug") {
      slugManualRef.current = true;
      next.slug = makeSlug(value);
    }
    return next;
  });
  const updateRow = (group, index, field, value) => setProduct((current) => ({
    ...current,
    [group]: current[group].map((row, i) => (i === index ? { ...row, [field]: value } : row)),
  }));
  const removeRow = (group,index) => setProduct((current)=>({...current,[group]:current[group].filter((_,i)=>i!==index)}));
  const addRow = (group,row) => setProduct((current)=>({...current,[group]:[...current[group],row]}));
  useEffect(() => {
    if (!location.state?.notice) return;
    setNotice(location.state.notice);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      setError("");
      try {
        if (isCreate) {
          const { items } = await fetchCategories({ page: 1, per_page: 50 });
          slugManualRef.current = false;
          setCategories(items);
          setRelatedProducts([]);
          setImages([]);
          setPendingBase(null);
          setPendingThumbnails([]);
          setProduct(emptyProduct);
          return;
        }

        const [{ items }, payload] = await Promise.all([
          fetchCategories({ page: 1, per_page: 50 }),
          fetchProduct(productId),
        ]);
        const record = productRecord(payload);
        setCategories(items);
        setRelatedProducts(relatedProductsFromPayload(payload));

        const categoryId = record.category_id ?? record.category?.id ?? "";
        let categoryName = record.category?.name || "";

        if (categoryId && !categoryName) {
          try {
            const category = await fetchCategory(categoryId);
            categoryName = category?.name || "";
          } catch {
            // Category may have been removed.
          }
        }

        setProduct({
          ...emptyProduct,
          ...record,
          nameZh: record.name_zh || "",
          overviewZh: record.overview_zh || "",
          meta_title: record.meta_title || "",
          meta_description: record.meta_description || "",
          meta_keyword: record.meta_keyword || "",
          categoryId: categoryId ? String(categoryId) : "",
          category: categoryName,
          popular: asFlag(record.most_popular_badge),
          featured: asFlag(record.featured),
          status: record.status ?? true,
          visible: record.visibility,
          whatsapp: record.whatsapp_enquiry,
          enquiryEnabled: record.enquiry_enabled,
          order: record.sort_order,
          relatedIds: relatedIdsFromValue(record.related_product_ids),
          specs: normalizeSpecs(record.specs),
          standards: normalizeStandards(record.standards),
          applications: normalizeApplications(record.applications),
        });
        setImages(record.images || []);
      } catch (requestError) {
        resolveApiError(requestError, setError, navigate);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId, isCreate, navigate]);

  useEffect(() => {
    if (!isCreate) return undefined;
    const categoryId = product.categoryId;
    if (!categoryId) {
      setRelatedProducts([]);
      return undefined;
    }

    let cancelled = false;
    fetchProducts({
      category_id: categoryId,
      per_page: 100,
      sort_by: "name",
      sort_order: "asc",
    }).then(({ products }) => {
      if (!cancelled) setRelatedProducts(relatedOptionsFromProducts(products));
    }).catch(() => {
      if (!cancelled) setRelatedProducts([]);
    });

    return () => {
      cancelled = true;
    };
  }, [isCreate, product.categoryId]);

  const saveProduct = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        name: product.name.trim(),
        name_zh: product.nameZh,
        overview: product.overview,
        overview_zh: product.overviewZh,
        slug: product.slug.trim() || makeSlug(product.name),
        sku: product.sku.trim(),
        category_id: product.categoryId ? Number(product.categoryId) : null,
        featured: asFlag(product.featured),
        most_popular_badge: !!product.popular,
        status: product.status ?? true,
        visibility: product.visible,
        sort_order: Number(product.order) || 0,
        whatsapp_enquiry: !!product.whatsapp,
        enquiry_enabled: !!product.enquiryEnabled,
        meta_title: product.meta_title || "",
        meta_description: product.meta_description || "",
        meta_keyword: product.meta_keyword || "",
        specs: product.specs,
        standards: product.standards,
        applications: product.applications,
        related_product_ids: relatedIdsFromValue(product.relatedIds),
      };

      if (isCreate) {
        let newId = null;
        try {
          const created = await createProduct(payload);
          newId = createdProductId(created);
          if (!newId) throw new Error("Unable to create this product.");
          if (pendingBase?.file) {
            await uploadProductImages(newId, [pendingBase.file], { is_base_image: true, is_thumbnail: false });
          }
          if (pendingThumbnails.length) {
            await uploadProductImages(newId, pendingThumbnails.map((item) => item.file), {
              is_base_image: false,
              is_thumbnail: true,
            });
          }
        } catch (createError) {
          if (newId) {
            try { await deleteProduct(newId); } catch { /* keep the original create/upload error */ }
          }
          throw createError;
        }
        if (pendingBase?.url) URL.revokeObjectURL(pendingBase.url);
        pendingThumbnails.forEach((item) => URL.revokeObjectURL(item.url));
        navigate(`/admin/products/${newId}`, { replace: true, state: { notice: "Product created." } });
        return;
      }

      await updateProduct(productId, payload);
      setNotice("Product details saved.");
    } catch (requestError) { resolveApiError(requestError, setError, navigate); } finally { setSaving(false); }
  };
  const refreshImages = async () => {
    const payload = await fetchProduct(productId);
    const record = productRecord(payload);
    setImages(record?.images || []);
  };

  const validateImageFiles = (files, input) => {
    const list = Array.from(files || []).filter((file) => file && file.size > 0);
    if (!list.length) return [];

    const invalid = list.find((file) => file.type && !IMAGE_TYPES.includes(file.type));
    if (invalid) {
      setError("Use JPG, PNG or WebP images only.");
      if (input) input.value = "";
      return [];
    }

    const tooLarge = list.find((file) => file.size > MAX_IMAGE_BYTES);
    if (tooLarge) {
      setError("Each image must be 5 MB or smaller. Larger files are rejected by the server as a failed upload.");
      if (input) input.value = "";
      return [];
    }

    return list;
  };

  const sortedImages = [...images].sort((left, right) => (Number(left?.sort_order) || 0) - (Number(right?.sort_order) || 0));
  const savedBaseImage = sortedImages.find((image) => asFlag(image.is_base_image)) || null;
  const baseImage = isCreate
    ? (pendingBase ? { id: "pending-base", url: pendingBase.url, name: pendingBase.file.name } : null)
    : savedBaseImage;
  const thumbnailImages = isCreate
    ? pendingThumbnails.map((item) => ({ id: item.key, url: item.url, name: item.file.name }))
    : sortedImages.filter((image) => image.id !== savedBaseImage?.id);

  const uploadImages = async (event, { is_base_image = false, is_thumbnail = false } = {}) => {
    const input = event.target;
    const files = validateImageFiles(input.files, input);
    const selected = is_base_image ? files.slice(0, 1) : files;
    if (!selected.length) return;

    if (isCreate) {
      if (is_base_image) {
        if (pendingBase?.url) URL.revokeObjectURL(pendingBase.url);
        setPendingBase({ file: selected[0], url: URL.createObjectURL(selected[0]) });
      } else {
        setPendingThumbnails((current) => [
          ...current,
          ...selected.map((file) => ({
            file,
            url: URL.createObjectURL(file),
            key: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          })),
        ]);
      }
      input.value = "";
      return;
    }

    setSaving(true);
    setError("");
    setNotice("");
    try {
      const previousBaseId = is_base_image ? savedBaseImage?.id : null;
      const saved = await uploadProductImages(productId, selected, { is_base_image, is_thumbnail });
      setImages(imagesFromPayload(saved, images));
      if (previousBaseId) {
        await deleteProductImage(productId, previousBaseId);
      }
      await refreshImages();
      setNotice(is_base_image ? "Base image uploaded." : "Thumbnail images uploaded.");
    } catch (requestError) {
      const failedUpload = requestError?.payload?.errors?.["images.0"]?.[0] || requestError?.message || "";
      if (/failed to upload/i.test(failedUpload)) {
        setError("The image failed to upload. Use JPG, PNG or WebP and keep the file under 5 MB.");
      } else {
        resolveApiError(requestError, setError, navigate);
      }
    } finally {
      setSaving(false);
      input.value = "";
    }
  };

  const toggleRelatedProduct = (id, checked) => {
    const value = Number(id);
    if (!Number.isFinite(value)) return;
    setProduct((current) => {
      const currentIds = relatedIdsFromValue(current.relatedIds);
      return {
        ...current,
        relatedIds: checked
          ? (currentIds.includes(value) ? currentIds : [...currentIds, value])
          : currentIds.filter((item) => item !== value),
      };
    });
  };

  const confirmDeleteImage = async () => {
    if (!pendingDelete?.id) return;
    if (isCreate || String(pendingDelete.id).startsWith("pending-")) {
      if (pendingDelete.id === "pending-base") {
        if (pendingBase?.url) URL.revokeObjectURL(pendingBase.url);
        setPendingBase(null);
      } else {
        setPendingThumbnails((current) => {
          const next = current.filter((item) => item.key !== pendingDelete.id);
          current.filter((item) => item.key === pendingDelete.id).forEach((item) => URL.revokeObjectURL(item.url));
          return next;
        });
      }
      setPendingDelete(null);
      return;
    }

    setDeleting(true);
    setError("");
    try {
      await deleteProductImage(productId, pendingDelete.id);
      setImages((current) => current.filter((item) => item.id !== pendingDelete.id));
      setPendingDelete(null);
      setNotice("Image deleted.");
      await refreshImages();
    } catch (requestError) {
      resolveApiError(requestError, setError, navigate);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="detail-editor">{isCreate ? "Loading…" : "Loading product…"}</div>;

  return <div className="detail-editor">
    {notice && <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>}
    {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
    <div className="detail-editor__top"><div><Link to="/admin/products"><Icon icon="ph:arrow-left" height={14} /> Back to products</Link><h2>{isCreate ? "New product" : product.name}</h2><p>{isCreate ? "Add a product to the catalogue." : `${product.sku || "No SKU"} · ${product.category || "No category"}`}</p></div><div><button className="ui-button ui-button--primary" onClick={saveProduct} disabled={saving}>{saving ? (isCreate ? "Creating…" : "Saving…") : (isCreate ? "Create product" : "Save changes")}</button></div></div>
    <div className="detail-editor__layout"><main className="detail-editor__main">
      <nav className="detail-editor__tabs">{[["content","Content & images"],["specs","Specifications"],["sidebar","Standards & related"]].map(([key,label])=><button className={tab===key?"active":""} key={key} onClick={()=>setTab(key)}>{label}</button>)}</nav>
      {tab==="content"&&<>
        <section className="detail-editor__card"><header><p>PAGE HEADER</p><h3>Product identity</h3></header><div className="detail-editor__two"><TextField label="English product name" value={product.name} onChange={e=>setField("name",e.target.value)}/><TextField label="Chinese product name" value={product.nameZh} onChange={e=>setField("nameZh",e.target.value)}/><TextField label="URL slug" value={product.slug} onChange={e=>setField("slug",e.target.value)}/><TextField label="SKU" value={product.sku} onChange={e=>setField("sku",e.target.value)}/><CategoryField categories={categories} value={product.categoryId} onChange={(event) => {
          const categoryId = event.target.value;
          const category = categories.find((item) => String(item.id) === categoryId);
          setProduct((current) => ({
            ...current,
            categoryId,
            category: category?.name || "",
            relatedIds: isCreate ? [] : current.relatedIds,
          }));
        }} /><label className="detail-editor__check"><input type="checkbox" checked={product.popular} onChange={e=>setField("popular",e.target.checked)}/><span><strong>Most Popular badge</strong><small>Show the amber badge on the main image.</small></span></label></div></section>
        <section className="detail-editor__card">
          <header>
            <p>PRODUCT GALLERY</p>
            <h3>Product images</h3>
          </header>
          <div className="detail-editor__image-group">
            <h4>Base image</h4>
            <div className="detail-editor__gallery-grid detail-editor__gallery-grid--base">
              {baseImage ? (
                <ImagePreviewCard
                  image={baseImage}
                  disabled={saving || deleting}
                  onDelete={setPendingDelete}
                />
              ) : (
                <ImageUploadTile
                  inputRef={baseImageInputRef}
                  label="Upload base image"
                  hint="JPG, PNG or WebP, max 5 MB."
                  disabled={saving}
                  onChange={(event) => uploadImages(event, { is_base_image: true, is_thumbnail: false })}
                />
              )}
            </div>
          </div>
          <div className="detail-editor__image-group">
            <h4>Thumbnails</h4>
            <div className="detail-editor__gallery-grid">
              {thumbnailImages.map((image) => (
                <ImagePreviewCard
                  key={image.id || image.url}
                  image={image}
                  disabled={saving || deleting}
                  onDelete={setPendingDelete}
                />
              ))}
              <ImageUploadTile
                inputRef={thumbnailInputRef}
                label="Upload thumbnails"
                hint="JPG, PNG or WebP, max 5 MB."
                multiple
                disabled={saving}
                onChange={(event) => uploadImages(event, { is_base_image: false, is_thumbnail: true })}
              />
            </div>
          </div>
        </section>
        <section className="detail-editor__card"><header><p>OVERVIEW · 产品概述</p><h3>Bilingual description</h3></header><TextField label="English overview" textarea value={product.overview} onChange={e=>setField("overview",e.target.value)}/><TextField label="Chinese overview" textarea value={product.overviewZh} onChange={e=>setField("overviewZh",e.target.value)}/></section>
      </>}
      {tab==="specs"&&<>
        <section className="detail-editor__card"><header><p>TECHNICAL SPECIFICATIONS · 技术规格</p><h3>Specification grid</h3></header><div className="detail-editor__rows">{product.specs.map((row,i)=><div className="detail-editor__row detail-editor__row--fields" key={i}><div className="detail-editor__row-fields detail-editor__row-fields--four"><input value={row.label} onChange={e=>updateRow("specs",i,"label",e.target.value)} placeholder="Label"/><input value={row.label_zh} onChange={e=>updateRow("specs",i,"label_zh",e.target.value)} placeholder="Chinese label"/><input value={row.value} onChange={e=>updateRow("specs",i,"value",e.target.value)} placeholder="Value"/><input value={row.value_zh} onChange={e=>updateRow("specs",i,"value_zh",e.target.value)} placeholder="Chinese value"/></div><button onClick={()=>removeRow("specs",i)} aria-label="Remove"><Icon icon="ph:x" height={14} /></button></div>)}</div><button className="detail-editor__add" onClick={()=>addRow("specs",{label:"",label_zh:"",value:"",value_zh:""})}><Icon icon="ph:plus" height={14} /> Add specification</button></section>
        <section className="detail-editor__card"><header><p>APPLICATIONS · 适用范围</p><h3>Application list</h3></header><div className="detail-editor__rows">{product.applications.map((item,i)=><div className="detail-editor__row" key={i}><b>{String(i+1).padStart(2,"0")}</b><div className="detail-editor__row-fields detail-editor__row-fields--two"><input value={item.name} onChange={e=>updateRow("applications",i,"name",e.target.value)} placeholder="Name"/><input value={item.name_zh} onChange={e=>updateRow("applications",i,"name_zh",e.target.value)} placeholder="Chinese name"/></div><button onClick={()=>removeRow("applications",i)} aria-label="Remove"><Icon icon="ph:x" height={14} /></button></div>)}</div><button className="detail-editor__add" onClick={()=>addRow("applications",{name:"",name_zh:""})}><Icon icon="ph:plus" height={14} /> Add application</button></section>
      </>}
      {tab==="sidebar"&&<>
        <section className="detail-editor__card"><header><p>STANDARDS · 标准认证</p><h3>Standards and certifications</h3></header><div className="detail-editor__rows">{product.standards.map((row,i)=><div className="detail-editor__row detail-editor__row--fields" key={i}><div className="detail-editor__row-fields detail-editor__row-fields--four"><input value={row.name} onChange={e=>updateRow("standards",i,"name",e.target.value)} placeholder="Label"/><input value={row.name_zh} onChange={e=>updateRow("standards",i,"name_zh",e.target.value)} placeholder="Chinese label"/><input value={row.value} onChange={e=>updateRow("standards",i,"value",e.target.value)} placeholder="Value"/><input value={row.value_zh} onChange={e=>updateRow("standards",i,"value_zh",e.target.value)} placeholder="Chinese value"/></div><button onClick={()=>removeRow("standards",i)} aria-label="Remove"><Icon icon="ph:x" height={14} /></button></div>)}</div><button className="detail-editor__add" onClick={()=>addRow("standards",{name:"",name_zh:"",value:"",value_zh:""})}><Icon icon="ph:plus" height={14} /> Add standard</button></section>
        <section className="detail-editor__card">
          <header>
            <p>RELATED PRODUCTS · 相关产品</p>
            <h3>Related products</h3>
          </header>
          <p className="detail-editor__related-hint">Select products from this category to show as related.</p>
          <RelatedProductsList
            products={relatedProducts}
            selectedIds={relatedIdsFromValue(product.relatedIds)}
            onToggle={toggleRelatedProduct}
          />
        </section>
      </>}
    </main><aside className="detail-editor__side"><section className="detail-editor__card"><header><p>STOREFRONT STATUS</p><h3>Publishing</h3></header><label className="detail-editor__toggle"><span>Visible online</span><input type="checkbox" checked={!!product.visible} onChange={e=>setField("visible",e.target.checked)}/></label><label className="detail-editor__toggle"><span>Featured</span><input type="checkbox" checked={!!product.featured} onChange={e=>setField("featured",e.target.checked)}/></label><label className="detail-editor__toggle"><span>WhatsApp enquiry</span><input type="checkbox" checked={!!product.whatsapp} onChange={e=>setField("whatsapp",e.target.checked)}/></label><label className="detail-editor__toggle"><span>Add to enquiry list</span><input type="checkbox" checked={!!product.enquiryEnabled} onChange={e=>setField("enquiryEnabled",e.target.checked)}/></label><TextField label="Display order" value={product.order ?? 0} onChange={e=>setField("order",e.target.value)}/><small className="detail-editor__saved">Changes are saved using the button above.</small></section><section className="detail-editor__card"><header><h3>SEO</h3></header><TextField label="Meta title" value={product.meta_title} onChange={e=>setField("meta_title",e.target.value)}/><TextField label="Meta description" textarea value={product.meta_description} onChange={e=>setField("meta_description",e.target.value)}/><TextField label="Meta keyword" value={product.meta_keyword} onChange={e=>setField("meta_keyword",e.target.value)}/></section></aside></div>
    <ConfirmDialog
      open={Boolean(pendingDelete)}
      title="Delete image"
      message={pendingDelete ? `Delete “${pendingDelete.name || "this image"}”? This cannot be undone.` : ""}
      confirming={deleting}
      onCancel={() => !deleting && setPendingDelete(null)}
      onConfirm={confirmDeleteImage}
    />
  </div>;
};
export default ProductDetailPage;
